import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageConstants } from '../utils/constants/language.constants';

const DEFAULT_FONT_FAMILY = 'Rubik, sans-serif';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  'es-MX': 'es-MX',
  de: 'de-DE',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-CA',
  it: 'it-IT',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-PT',
  tr: 'tr-TR',
  ko: 'ko-KR',
  ja: 'ja-JP',
  nl: 'nl-NL',
  'zh-TW': 'zh-TW',
} as const;

// Languages whose dates spell the month out in full with no period (e.g. Turkish "7 Eylül 2026", not "7 Eyl 2026").
const FULL_MONTH_NAME_LANGUAGES = new Set(['tr']);

// Japanese dates are zero-padded numeric year/month/day (e.g. "2026/08/21"), matching CLDR's ja short date pattern.
const JAPANESE_LANGUAGE = 'ja';
const JAPANESE_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
} as const;

const ITALIAN_MONTH_ABBREVIATIONS: Record<string, string> = {
  gen: 'gen.',
  feb: 'feb.',
  mar: 'mar.',
  apr: 'apr.',
  mag: 'mag.',
  giu: 'giu.',
  lug: 'lug.',
  ago: 'ago.',
  set: 'set.',
  ott: 'ott.',
  nov: 'nov.',
  dic: 'dic.',
} as const;

const GERMAN_MONTH_ABBREVIATIONS: Record<string, string> = {
  Jan: 'Jan.',
  Feb: 'Feb.',
  Mär: 'März',
  Apr: 'Apr.',
  Mai: 'Mai',
  Jun: 'Jun.',
  Jul: 'Jul.',
  Aug: 'Aug.',
  Sep: 'Sept.',
  Okt: 'Okt.',
  Nov: 'Nov.',
  Dez: 'Dez.',
} as const;

const SPANISH_MONTH_ABBREVIATIONS: Record<string, string> = {
  ene: 'ene.',
  feb: 'feb.',
  mar: 'mar.',
  abr: 'abr.',
  may: 'may.',
  jun: 'jun.',
  jul: 'jul.',
  ago: 'ago.',
  sept: 'sept.',
  oct: 'oct.',
  nov: 'nov.',
  dic: 'dic.',
} as const;

// Dutch abbreviated months take a period (e.g. "6 okt. 2026"); "mei" is the full word and takes none.
const DUTCH_LANGUAGE = 'nl';
const DUTCH_MONTH_ABBREVIATIONS: Record<string, string> = {
  jan: 'jan.',
  feb: 'feb.',
  mrt: 'mrt.',
  apr: 'apr.',
  mei: 'mei',
  jun: 'jun.',
  jul: 'jul.',
  aug: 'aug.',
  sep: 'sep.',
  okt: 'okt.',
  nov: 'nov.',
  dec: 'dec.',
} as const;

// Dutch drops the thousands separator in four-digit numbers ("2052", but "25.000").
// 'min2' is ES2023 Intl (Chrome 106+, Safari 15.4+, Firefox 116+); the project's TS lib is ES2022.
const DUTCH_NUMBER_GROUPING = { useGrouping: 'min2' } as unknown as Intl.NumberFormatOptions;

// Traditional Chinese (Taiwan) dates run year → month → day with unpadded 年/月/日 (e.g. "2026年10月6日"),
// times use the 12-hour clock with 上午/下午 before an unpadded hour and a half-width colon (e.g. "下午3:30").
const TAIWANESE_LANGUAGE = 'zh-TW';

// Taiwanese durations write numbers directly against CJK units ("1小時30分鐘", "45秒"). Only spaces between a
// digit and a CJK character are removed, so untranslated English fallbacks keep theirs ("5 min 30 sec").
const DIGIT_BEFORE_CJK = /(\d)\s+([\u3400-\u9fff])/g;
const CJK_BEFORE_DIGIT = /([\u3400-\u9fff])\s+(\d)/g;

// Taiwanese tables and compact UI use the short numeric date, CLDR zh-Hant-TW "y/M/d" (e.g. "2026/10/27"),
// and CSV exports use ISO dates (e.g. "2026-10-27"); prose and labels keep "2026年10月27日".

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  constructor(private translocoService: TranslocoService) {}

  formatDate(date: Date | string | number): string {
    if (!date) return '';

    const dateObj = this.convertToUtcDate(date);
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();

    const day = dateObj.getUTCDate();
    const month = this.formatMonthAbbreviation(dateObj, localeId, currentLang);
    const year = dateObj.getUTCFullYear();

    return this.formatDateString(day.toString(), month, year, currentLang);
  }

  private convertToUtcDate(date: Date | string | number): Date {
    const tempDate = new Date(date);
    if (isNaN(tempDate.getTime())) {
      return tempDate;
    }

    const useUtcMethods = typeof date === 'string' && date.includes('T') && date.includes('Z');

    const year = useUtcMethods ? tempDate.getUTCFullYear() : tempDate.getFullYear();
    const month = useUtcMethods ? tempDate.getUTCMonth() : tempDate.getMonth();
    const day = useUtcMethods ? tempDate.getUTCDate() : tempDate.getDate();

    return new Date(Date.UTC(year, month, day, 12, 0, 0));
  }

  formatDateTime(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    return this.formatDateTimeWithSeparator(dateObj, this.getDateTimeSeparator(this.getCurrentLanguage()));
  }

  formatShortDateTimeWithTimezone(date: Date | string | number, timeZone: string = 'America/New_York'): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();
    const use12Hour = this.shouldUse12HourFormat(currentLang);
    const tzOpt = timeZone;

    const opts: Intl.DateTimeFormatOptions = {
      timeZone: tzOpt,
      month: currentLang === JAPANESE_LANGUAGE ? '2-digit' : currentLang === TAIWANESE_LANGUAGE ? 'numeric' : this.getMonthStyle(currentLang),
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: use12Hour,
      timeZoneName: 'short',
    };

    const parts = new Intl.DateTimeFormat(localeId, opts).formatToParts(dateObj);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

    const month = this.applyLanguageSpecificMonthAbbreviation(get('month'), currentLang);
    const dateStr = this.formatDateString(get('day'), month, Number(get('year')) || dateObj.getFullYear(), currentLang);

    const timeFormatted = new Intl.DateTimeFormat(localeId, {
      timeZone: tzOpt,
      hour: currentLang === 'ko' || currentLang === TAIWANESE_LANGUAGE ? 'numeric' : '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: use12Hour,
    }).format(dateObj);
    const keepsColon = currentLang === JAPANESE_LANGUAGE || currentLang === TAIWANESE_LANGUAGE;
    const timeSeparated = keepsColon ? timeFormatted : timeFormatted.replace(/:/g, '.');
    const timeStr = this.applyCountrySpecificTimeFormatting(timeSeparated, currentLang);

    // Taiwanese shows the UTC offset (e.g. "GMT-6"), never the abbreviation "CST", which is ambiguous
    // between US Central and China Standard Time.
    const timeZoneName = currentLang === TAIWANESE_LANGUAGE ? 'shortOffset' : 'short';
    const tzStr =
      new Intl.DateTimeFormat('en-US', { timeZone: tzOpt, timeZoneName })
        .formatToParts(dateObj)
        .find((p) => p.type === 'timeZoneName')?.value ?? '';

    const separator = this.getDateTimeSeparator(currentLang);
    return tzStr ? `${dateStr}${separator}${timeStr} ${tzStr}` : `${dateStr}${separator}${timeStr}`;
  }

  formatDateShort(date: Date | string | number): string {
    if (!date) return '';

    const localeId = this.getCurrentLocaleId();
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const currentLang = this.getCurrentLanguage();

    const month = this.formatMonthAbbreviation(dateObj, localeId, currentLang);
    const day = dateObj.getUTCDate();

    if (currentLang === 'ko') {
      return `${month}. ${day}.`;
    }
    if (currentLang === JAPANESE_LANGUAGE) {
      return `${month}/${day.toString().padStart(2, '0')}`;
    }
    if (currentLang === DUTCH_LANGUAGE) {
      return `${day} ${month}`;
    }
    if (currentLang === TAIWANESE_LANGUAGE) {
      return `${month}月${day}日`;
    }

    return `${month} ${day}`;
  }

  formatDateLong(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    return dateObj.toLocaleDateString(
      this.getCurrentLocaleId(),
      this.getLongDateOptions(this.getCurrentLanguage(), 'numeric')
    );
  }

  formatDateTimeLong(date: Date | string | number): string {
    return this.formatDateTimeWithOptions(date, {
      day: 'numeric',
      hour: 'numeric',
    });
  }

  formatSessionDateTime(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();

    let month: string;
    let day: string;
    if (currentLang === 'ko' || currentLang === TAIWANESE_LANGUAGE) {
      month = (dateObj.getMonth() + 1).toString();
      day = dateObj.getDate().toString();
    } else if (currentLang === JAPANESE_LANGUAGE) {
      month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      day = dateObj.getDate().toString().padStart(2, '0');
    } else {
      const monthAbbr = dateObj.toLocaleDateString(localeId, { month: this.getMonthStyle(currentLang) });
      month = this.applyLanguageSpecificMonthAbbreviation(monthAbbr, currentLang);
      // Dutch doesn't zero-pad the day before a month name ("6 okt. 2026", not "06 okt. 2026").
      day = currentLang === DUTCH_LANGUAGE ? dateObj.getDate().toString() : dateObj.getDate().toString().padStart(2, '0');
    }
    const year = dateObj.getFullYear();

    const dateStr =
      currentLang === TAIWANESE_LANGUAGE
        ? this.formatNumericDate(year, Number(month), Number(day))
        : this.formatDateString(day, month, year, currentLang);
    const hourFormat = currentLang === 'ko' || currentLang === TAIWANESE_LANGUAGE ? 'numeric' : '2-digit';
    const timeStr = this.formatTimeInternal(dateObj, hourFormat, false);

    return `${dateStr} ${timeStr}`;
  }

  /** Date for a table cell: the short numeric date in Taiwanese ("2026/10/27"), otherwise {@link formatDate}. */
  formatTableDate(date: Date | string | number): string {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE) {
      return this.formatDate(date);
    }
    if (!date) return '';
    const dateObj = this.convertToUtcDate(date);
    if (isNaN(dateObj.getTime())) return '';
    return this.formatNumericDate(dateObj.getUTCFullYear(), dateObj.getUTCMonth() + 1, dateObj.getUTCDate());
  }

  /** Date for a table cell: the short numeric date in Taiwanese ("2026/10/27"), otherwise {@link formatDateLong}. */
  formatTableDateLong(date: Date | string | number): string {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE) {
      return this.formatDateLong(date);
    }
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';
    return this.formatNumericDate(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
  }

  /** Date-time for a table cell: "2026/10/27 下午3:30" in Taiwanese, otherwise {@link formatDateTime}. */
  formatTableDateTime(date: Date | string | number): string {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE) {
      return this.formatDateTime(date);
    }
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';
    const dateStr = this.formatNumericDate(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
    return `${dateStr} ${this.formatTime(dateObj)}`;
  }

  /**
   * ISO date for a CSV export cell in Taiwanese ("2026-10-27"), on the same calendar day as {@link formatDate}.
   * Returns undefined for other languages, which export the displayed value.
   */
  formatCsvDate(date: Date | string | number | null | undefined): string | undefined {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE || !date) return undefined;
    const dateObj = this.convertToUtcDate(date);
    if (isNaN(dateObj.getTime())) return undefined;
    return this.formatIsoDate(dateObj.getUTCFullYear(), dateObj.getUTCMonth() + 1, dateObj.getUTCDate());
  }

  /**
   * ISO date and 24-hour local time for a CSV export cell in Taiwanese ("2026-10-27 15:30").
   * Returns undefined for other languages, which export the displayed value.
   */
  formatCsvDateTime(date: Date | string | number | null | undefined): string | undefined {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE) return undefined;
    const dateObj = this.toDateObject(date);
    if (!dateObj) return undefined;
    const dateStr = this.formatIsoDate(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}`;
  }

  private formatNumericDate(year: number, month: number, day: number): string {
    return `${year}/${month}/${day}`;
  }

  private formatIsoDate(year: number, month: number, day: number): string {
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }

  private formatDateTimeWithOptions(
    date: Date | string | number,
    options: { day: 'numeric' | '2-digit'; hour: 'numeric' | '2-digit' }
  ): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const dateStr = dateObj.toLocaleDateString(
      localeId,
      this.getLongDateOptions(this.getCurrentLanguage(), options.day)
    );

    const hourFormat = this.getCurrentLanguage() === JAPANESE_LANGUAGE ? '2-digit' : options.hour;
    const timeStr = this.formatTimeInternal(dateObj, hourFormat, false);

    return `${dateStr} ${timeStr}`;
  }

  /**
   * Format date and time with custom separator and options
   * @param date - Date to format
   * @param separator - Separator between date and time (default: ', ')
   * @param dateOptions - Intl.DateTimeFormatOptions for date formatting
   * @param timeOptions - Intl.DateTimeFormatOptions for time formatting
   * @returns Formatted date-time string
   */
  formatCustomDateTime(
    date: Date | string | number,
    separator: string = ', ',
    dateOptions?: Intl.DateTimeFormatOptions,
    timeOptions?: Intl.DateTimeFormatOptions
  ): string {
    if (!date) return '';

    const localeId = this.getCurrentLocaleId();
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const currentLang = this.getCurrentLanguage();
    const defaultDateOptions = this.getLongDateOptions(currentLang, 'numeric');

    const defaultTimeOptions: Intl.DateTimeFormatOptions =
      currentLang === JAPANESE_LANGUAGE
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : { hour: 'numeric', minute: '2-digit', hour12: true };

    const dateStr = dateObj.toLocaleDateString(
      localeId,
      dateOptions || defaultDateOptions
    );

    const timeStr = dateObj.toLocaleTimeString(
      localeId,
      timeOptions || defaultTimeOptions
    );

    return `${dateStr}${separator}${timeStr}`;
  }

  formatNumber(value: number, decimals?: number): string {
    const localeId = this.getCurrentLocaleId();
    const grouping = this.getCurrentLanguage() === DUTCH_LANGUAGE ? DUTCH_NUMBER_GROUPING : {};

    if (decimals !== undefined) {
      return value.toLocaleString(localeId, {
        ...grouping,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    return value.toLocaleString(localeId, grouping);
  }

  formatScoreValue(score: string | null | undefined, decimals: number = 1): string {
    if (score === null || score === undefined) {
      return '_';
    }
    
    const numValue = parseFloat(score);
    if (isNaN(numValue)) {
      return score;
    }
    
    const rounded = Math.round(numValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    
    const isWholeNumber = rounded % 1 === 0;
    return this.formatNumber(rounded, isWholeNumber ? 0 : decimals);
  }

  formatPercentage(value: number, decimals: number = 1): string {
    const localeId = this.getCurrentLocaleId();
    return (value / 100).toLocaleString(localeId, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /**
   * Decimal places already present in `value`, capped at `maxDecimals`
   * (defaults to no cap — safe for formatNumber, which has no rounding
   * risk at any precision. Pass a lower cap, e.g. 2, when feeding
   * formatPercentage, which divides by 100 internally and can pick up
   * floating-point rounding artifacts at very high precision).
   * Lets callers localize a number without changing how many digits it shows.
   */
  getDecimalPlaces(value: number, maxDecimals: number = Infinity): number {
    if (!Number.isFinite(value) || Number.isInteger(value)) return 0;
    const decimals = value.toString().split('.')[1]?.length ?? 0;
    return Math.min(decimals, maxDecimals);
  }

  getCurrentLocaleId(): string {
    const currentLang = this.translocoService.getActiveLang();
    return LOCALE_MAP[currentLang] || 'en-US';
  }

  getCurrentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  getFontFamily(): string {
    const override = LanguageConstants.FONT_FAMILY_OVERRIDES[this.getCurrentLanguage()];
    return override ? `Rubik, "${override}", sans-serif` : DEFAULT_FONT_FAMILY;
  }

  formatTime(
    date: Date | string | number | null | undefined,
    includeSeconds: boolean = false
  ): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const hourFormat = this.shouldUse12HourFormat(this.getCurrentLanguage())
      ? 'numeric'
      : '2-digit';

    return this.formatTimeInternal(dateObj, hourFormat, includeSeconds);
  }

  private formatTimeInternal(
    dateObj: Date,
    hourFormat: 'numeric' | '2-digit',
    includeSeconds: boolean = false
  ): string {
    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();
    const use12Hour = this.shouldUse12HourFormat(currentLang);

    const options: Intl.DateTimeFormatOptions = {
      hour: hourFormat,
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' }),
      hour12: use12Hour,
    };

    const formattedTime = dateObj.toLocaleTimeString(localeId, options);

    return this.applyCountrySpecificTimeFormatting(formattedTime, currentLang);
  }

  formatTimeWithSeconds(
    date: Date | string | number | null | undefined
  ): string {
    return this.formatTime(date, true);
  }

  private formatDateTimeWithSeparator(
    dateObj: Date,
    separator: string
  ): string {
    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();

    // The time below is local, so the date must use local fields too; reading UTC fields
    // shows the wrong day near midnight (e.g. 00:00-08:59 in Japan, UTC+9).
    const localDate = new Date(
      Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 12, 0, 0)
    );
    const day = localDate.getUTCDate();
    const month = this.formatMonthAbbreviation(localDate, localeId, currentLang);
    const year = localDate.getUTCFullYear();

    const dateStr = this.formatDateString(day.toString(), month, year, currentLang);

    const timeStr = this.formatTime(dateObj);

    return `${dateStr}${separator}${timeStr}`;
  }

  private isValidDate(
    date: Date | string | number | null | undefined
  ): boolean {
    if (!date || (typeof date === 'string' && date.trim() === '')) {
      return false;
    }
    const dateObj =
      typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;
    return !isNaN(dateObj.getTime());
  }

  private toDateObject(
    date: Date | string | number | null | undefined
  ): Date | null {
    if (!this.isValidDate(date)) {
      return null;
    }
    return typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : (date as Date);
  }

  // Taiwanese full date-times join the date and time with a single space (e.g. "2026年10月27日 下午3:30").
  private getDateTimeSeparator(currentLang: string): string {
    return currentLang === TAIWANESE_LANGUAGE ? ' ' : ' • ';
  }

  private shouldUse12HourFormat(currentLang: string): boolean {
    return currentLang === 'en' || currentLang === 'en-US' || currentLang === 'ko' || currentLang === TAIWANESE_LANGUAGE;
  }

  private applyCountrySpecificTimeFormatting(
    timeString: string,
    currentLang: string
  ): string {
    switch (currentLang) {
      case 'fr-FR':
        return timeString.replace(':', ' h ');

      case 'fr-CA':
        return timeString.replace(' h ', ':');

      case 'de':
        return timeString;

      case 'pt-BR':
      case 'pt-PT':
        return timeString;

      case 'en':
      case 'en-US':
        return timeString;

      default:
        return timeString;
    }
  }

  private formatMonthAbbreviation(
    dateObj: Date,
    localeId: string,
    currentLang: string
  ): string {
    const utcDate = new Date(
      Date.UTC(
        dateObj.getUTCFullYear(),
        dateObj.getUTCMonth(),
        dateObj.getUTCDate(),
        12,
        0,
        0
      )
    );

    if (currentLang === 'ko' || currentLang === TAIWANESE_LANGUAGE) {
      return (utcDate.getUTCMonth() + 1).toString();
    }
    if (currentLang === JAPANESE_LANGUAGE) {
      return (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
    }

    const monthAbbr = utcDate.toLocaleDateString(localeId, { month: this.getMonthStyle(currentLang) });
    return this.applyLanguageSpecificMonthAbbreviation(monthAbbr, currentLang);
  }

  private getMonthStyle(currentLang: string): 'long' | 'short' {
    return FULL_MONTH_NAME_LANGUAGES.has(currentLang) ? 'long' : 'short';
  }

  private getLongDateOptions(
    currentLang: string,
    day: 'numeric' | '2-digit'
  ): Intl.DateTimeFormatOptions {
    if (currentLang === JAPANESE_LANGUAGE) {
      return JAPANESE_DATE_OPTIONS;
    }
    return { year: 'numeric', month: 'long', day };
  }

  private applyLanguageSpecificMonthAbbreviation(
    monthAbbr: string,
    currentLang: string
  ): string {
    if (currentLang === 'it') {
      return ITALIAN_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    if (currentLang === 'de') {
      return GERMAN_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    if (currentLang === 'es-MX') {
      return SPANISH_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    if (currentLang === DUTCH_LANGUAGE) {
      return DUTCH_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    return monthAbbr;
  }

  private formatDateString(
    day: string,
    month: string,
    year: number,
    currentLang: string
  ): string {
    if (currentLang === 'de') {
      return `${day}. ${month} ${year}`;
    }
    if (currentLang === 'en') {
      return `${month} ${day}, ${year}`;
    }
    if (currentLang === 'ko') {
      return `${year}. ${month}. ${day}.`;
    }
    if (currentLang === JAPANESE_LANGUAGE) {
      return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    }
    if (currentLang === TAIWANESE_LANGUAGE) {
      return `${year}年${month}月${day}日`;
    }
    return `${day} ${month} ${year}`;
  }

  formatDurationInSecondsWithLabel(
    seconds: number | null | undefined,
    includeZeroSeconds = false
  ): string {
    if (seconds == null || isNaN(seconds)) return '—';
  
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
  
    const minLabel = this.translocoService.translate(
      minutes === 1 ? 'MINUTE' : 'MINUTES',
      {},
      'reportingV2'
    );
    const secLabel = this.translocoService.translate(
      remainingSeconds === 1 ? 'SECOND' : 'SECONDS',
      {},
      'reportingV2'
    );
  
    // Japanese writes numbers directly against their unit, with no space (e.g. "5分30秒").
    const sp = this.getCurrentLanguage() === JAPANESE_LANGUAGE ? '' : ' ';

    if (minutes > 0 && remainingSeconds > 0) {
      return this.formatDurationSpacing(`${minutes}${sp}${minLabel}${sp}${remainingSeconds}${sp}${secLabel}`);
    } else if (minutes > 0 && includeZeroSeconds) {
      const paddedSeconds = remainingSeconds.toString().padStart(2, '0');
      return this.formatDurationSpacing(`${minutes}${sp}${minLabel}${sp}${paddedSeconds}${sp}${secLabel}`);
    } else if (minutes > 0) {
      return this.formatDurationSpacing(`${minutes}${sp}${minLabel}`);
    } else {
      return this.formatDurationSpacing(`${remainingSeconds}${sp}${secLabel}`);
    }
  }

  /**
   * Removes the spaces between numbers and CJK unit labels for Taiwanese
   * (e.g. "1 小時 30 分鐘" → "1小時30分鐘"); other languages are returned unchanged.
   */
  formatDurationSpacing(duration: string): string {
    if (this.getCurrentLanguage() !== TAIWANESE_LANGUAGE) {
      return duration;
    }
    return duration.replace(DIGIT_BEFORE_CJK, '$1$2').replace(CJK_BEFORE_DIGIT, '$1$2');
  }
}
